import React, {
  FC,
  forwardRef,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import { DatatableFocusState } from "types/Focus";
import { Pojo } from "types/Galaxy";
import Axios, { CancelTokenSource } from "axios";
import { convertTermByOperator, GSComparison, RSQLOperator } from "utils/query.utils";
import { GSBuilder } from "utils/query.utils";
import { GS } from "utils/query.utils";
import { RSQLCriteria } from "rsql-criteria-typescript";
import { DatatableSort } from "composants/datatable/Table";
import { TypeSimpleComponent, TypeSimpleComponentValue } from "enum";
import { ComponentReadOnly, ComponentState, Item } from "types/Component";
import { AutoSizer } from "react-virtualized";

import { FilterBar, PagedResource } from "types/Search";
import {
  ColDef,
  ColumnMovedEvent,
  CustomEditorModule,
  GetRowIdParams,
  GridReadyEvent,
  IServerSideGetRowsParams,
  ModuleRegistry,
  RowClickedEvent,
  RowStyleModule,
  RowClassRules,
  ServerSideTransaction,
  ValueSetterParams,
  CellClassParams,
  ColumnResizedEvent,
  LargeTextEditorModule,
  FilterModel,
  AdvancedFilterModel
} from "ag-grid-community";
import {
  SetFilterModule,
  ColumnMenuModule,
  ContextMenuModule,
  MultiFilterModule,
  ValidationModule,
  DateFilterModule,
  NumberFilterModule,
  TextFilterModule,
  ServerSideRowModelModule,
  LocaleModule,
  GetMainMenuItemsParams,
  GetContextMenuItemsParams,
  CellSelectionModule,
  RowSelectionModule,
  RowSelectionOptions,
  SelectionChangedEvent,
  ServerSideRowModelApiModule,
  CellStyleModule,
  TextEditorModule,
  NumberEditorModule,
  DateEditorModule,
  RowGroupingModule
} from "ag-grid-enterprise";

import {
  AG_GRID_LOCALE_FR,
  AG_GRID_LOCALE_DE,
  AG_GRID_LOCALE_IT,
  AG_GRID_LOCALE_EN
} from "@ag-grid-community/locale";

import { tw } from "twind";
import { Fa } from "composants/Icon";
import parse from "date-fns/parse";
import MenuItem from "./ProcessMenuItem";
import { getProcessusDefinition } from "api/processus";
import MiniExpertMenuItem from "./MiniExpertMenuItem";
import InfoMenuItem from "./InfoMenuItem";
import { useTranslation } from "react-i18next";
import { ProcessusProvider } from "composants/processus/ProcessusProvider";
import { track } from "tracking";
import SatelliteMenuButton from "composants/satellite/SatelliteMenuButton";
import { existList } from "api/satellites";
import { buildCellEditorAutocomplete } from "./CellEditorAutocomplete";
import useInteractions from "hooks/useInteractions";
import AgTableToolbar from "./AgTableToolbar";
import { preRecord } from "api";
import { Switch } from "@axin-org/comet";
import { parseSortStr } from "containers/datatable/datatableReducer";
import OelForTable from "composants/oel/OelForTable";

import "./AgGrid.css";

export const MIN_BATCH_SIZE = 25;

interface DatatableAGGridProps {
  ref: React.RefObject<AgGridReact>;
  sjmoCode: string;
  tableName: string;
  ctrlkey: string;
  additionnalClause?: RSQLCriteria;
  nodeType?: string;
  processIdDrop?: string;
  definition: {
    focus: DatatableFocusState[];
    columns: ComponentState[];
    selectedFocus: string | null;
  };
  currentFocus: DatatableFocusState | undefined;
  dirtyRows?: Record<string, Partial<Pojo>>;
  insertable?: boolean;
  wviState?: WviState;
  getRSQLFilter: (
    interactions: Record<string, any>,
    searchValues: AGFilter,
    sortValues: Record<string, DatatableSort>,
    breakColumns: string[],
    additionnalClause: RSQLCriteria | undefined,
    columns: Record<string, ComponentState>
  ) => RSQLCriteria;
  fetchData: (props: {
    sjmoCode: string;
    tableName: string;
    ctrlkey: string;
    currentFocus?: DatatableFocusState;
    query: string;
    startIndex: number;
    stopIndex: number;
    reset: boolean;
    cancelToken: CancelTokenSource;
    oelColumns: string[];
    filterBar: FilterBar | undefined;
    groupBy?: string;
  }) => Promise<PagedResource<Pojo>>;
  onChange?: (params: ValueSetterParams<Pojo, string>) => void;
  saveRows?: (rows: Partial<Pojo>[]) => Promise<void>;
  deleteRows?: (rows: Partial<Pojo>[]) => Promise<void>;
  onAfterSaveDatatable?: () => void;
  onAfterDeleteDatatable?: () => void;
  onAfterProcessListeGeneric?: () => void;
  onSaveFocusPerso: () => void;
  onRazFocusPerso: () => void;
  onFocusChange: (selected: string) => void;
  onColumnMoved: (event: ColumnMovedEvent) => void;
  onRowClick?: (rowClickEvent: RowClickedEvent) => void;
  setDirtyRows?: (
    dirtyRows:
      | Record<string, Partial<Pojo>>
      | ((prev: Record<string, Partial<Pojo>>) => Record<string, Partial<Pojo>>)
  ) => void;
  onColumnResized?: (params: ColumnResizedEvent) => void;
}

export interface Filtermodel {
  filter: string;
  filterType: string;
  type: string;
  operator?: string;
  conditions?: Filtermodel[];
  dateFrom?: string;
  dateTo?: string;
  filterTo?: string;
  values?: string[];
}

export type AGFilter = Record<string, Filtermodel>;

export interface WviState {
  [rowIndex: number]: { [field: string]: { type: string; message?: string } };
}

function getFilterAllField(definition: { columns: ComponentState[] }, filterAllFields: string) {
  const filterAllFieldsModel = GSBuilder.OR(
    ...definition.columns
      .filter(
        c =>
          c.typeCompo !== TypeSimpleComponent.OEL &&
          c.typeCompo !== TypeSimpleComponent.CHECKBOX &&
          c.compoVisible
      )
      .map(column => {
        if (column.typeCompo === TypeSimpleComponent.AUTO_COMPLETE) {
          return mapcriteriaForGS(
            column,
            {
              filter: filterAllFields,
              filterType: "text",
              type: "contains"
            },
            column.column
          );
        }

        const f = mapOneFilter(
          {
            filter: filterAllFields,
            filterType: mapTypeCompoToFilterType(column.typeCompo as TypeSimpleComponentValue),
            type: "contains"
          },
          column.column
        );
        return f;
      })
  );
  return GSBuilder.toFilter(filterAllFieldsModel);
}

function explainFilter(
  filterModel: Record<string, FilterModel | AdvancedFilterModel | null>,
  allColumnRef: Record<string, ComponentState>,
  localeText: Record<string, string>
) {
  const result: string[] = [];

  Object.keys(filterModel).forEach(key => {
    const currentFilter = (filterModel as AGFilter)[key];

    if (currentFilter.conditions) {
      result.push(
        currentFilter.conditions
          .map((condition: Filtermodel) =>
            ExplainOneFilter(condition, allColumnRef[key], localeText)
          )
          .join(
            ` ${
              localeText[
                currentFilter.operator === "AND" ? "advancedFilterAnd" : "advancedFilterOr"
              ]
            } `
          )
      );
    } else {
      result.push(ExplainOneFilter(currentFilter, allColumnRef[key], localeText));
    }
  });

  return result;
}

function ExplainOneFilter(
  filterModel: FilterModel,
  ColumnDef: ComponentState,
  localeText: Record<string, string>
) {
  switch (filterModel.filterType) {
    case "set": {
      const operatorLabel = localeText["equals"];
      const orLabel = localeText["orCondition"];

      if (ColumnDef.sysDomaineChoices) {
        return `${ColumnDef.label} ${operatorLabel} ${filterModel.values
          ?.map((v: string) => ColumnDef.sysDomaineChoices?.find(d => d.value === v)?.label)
          ?.join(` ${orLabel} `)}`;
      } else {
        return `${ColumnDef.label} ${operatorLabel} ${filterModel.values
          ?.map((v: string) => `"${v}"`)
          ?.join(` ${orLabel} `)}`;
      }
    }

    case "date": {
      const type = (() => {
        switch (filterModel.type) {
          case "lessThan":
            return "before";
          case "greaterThan":
            return "after";
          default:
            return filterModel.type;
        }
      })();
      const operatorLabel = localeText[type];

      const dateFrom = filterModel.dateFrom ? formatdateALaRache(filterModel.dateFrom) : "";
      const dateTo = filterModel.dateTo ? formatdateALaRache(filterModel.dateTo) : "";
      return `${ColumnDef.label} ${operatorLabel} ${dateFrom} ${
        dateFrom && dateTo ? localeText["advancedFilterAnd"] : ""
      } ${dateTo}`;
    }

    case "number": {
      const operatorLabel = localeText[filterModel.type];
      const value = filterModel.filter;
      return `${ColumnDef.label} ${operatorLabel} ${value} ${
        value && filterModel.filterTo ? localeText["advancedFilterAnd"] : ""
      } ${filterModel.filterTo}`;
    }

    default: {
      const value = filterModel.filter;
      const operator = filterModel.type;
      return `${ColumnDef.label} ${localeText[operator]} ${value ? `"${value}"` : ""}`;
    }
  }
}

function formatdateALaRache(date: string) {
  return date
    .replace("00:00:00", "")
    .replaceAll("-", "/")
    .replace("T", " ");
}

function getGroupByFilter(
  groupBy: ColDef[],
  groupKeys: any[],
  rsql: RSQLCriteria,
  originGroupKeys: any[]
) {
  const groupFilters = groupBy.slice(0, groupKeys.length).map((col, index) => {
    if (typeof originGroupKeys[index] === "object") {
      return GSBuilder.Comparison(`${col.field}.id`, "OPER_EQ", (originGroupKeys[index] as any).id);
    }

    return GSBuilder.Comparison(col.field as string, "OPER_EQ", originGroupKeys[index]);
  });

  if (groupFilters.length > 0) {
    const groupFilter = GSBuilder.AND(...groupFilters);
    const filtered = GSBuilder.toFilter(groupFilter);
    return filtered;
  }
  return undefined;
}

ModuleRegistry.registerModules([
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  CellSelectionModule,
  ColumnMenuModule,
  ContextMenuModule,
  DateFilterModule,
  LocaleModule,
  MultiFilterModule,
  NumberFilterModule,
  SetFilterModule,
  TextFilterModule,
  ValidationModule,
  RowSelectionModule,
  CellStyleModule,
  TextEditorModule,
  NumberEditorModule,
  DateEditorModule,
  CustomEditorModule,
  RowGroupingModule,
  RowStyleModule,
  LargeTextEditorModule
]);

const ROW_SELECTION_MODE: RowSelectionOptions | "single" | "multiple" = {
  mode: "multiRow",
  headerCheckbox: false
};

function getRowId(params: GetRowIdParams) {
  return params.data.id ?? params.data.uuid;
}

function getValueFromSd(p: { value: Item }) {
  return p.value.value;
}

function getLabelFromSd(p: { value: Item }) {
  return p.value.label;
}

function getPropsByTypeCompo({
  typeCompo,
  tableName,
  sjmoCode,
  ctrlKey,
  componentID,
  columnIndex,
  compo
}: {
  typeCompo: string;
  tableName: string;
  sjmoCode: string;
  ctrlKey: string;
  componentID: string;
  columnIndex: number;
  compo: ComponentState;
}): {
  filter: string | boolean;
  filterParams?: Record<string, any>;
  filterValues?: any[];
  renderer?: FC<any>;
  filterRenderer?: FC<any>;
  cellEditor?: string | FC<any>;
  cellEditorPopup?: boolean;
  valueFormatter?: (params: any) => string;
  valueGetter?: (params: any) => string;
} {
  switch (typeCompo) {
    case TypeSimpleComponent.AUTO_COMPLETE:
      return {
        filter: "agTextColumnFilter",
        cellEditor: buildCellEditorAutocomplete({
          tableName,
          sjmoCode,
          ctrlKey,
          componentID,
          columnIndex,
          compo
        }),
        valueFormatter: (params: any) => params.value?.label || params.value,
        valueGetter: (params: any) => params.value?.id
      };
    case TypeSimpleComponent.AUTO_COMPLETE_VIEW:
    case TypeSimpleComponent.AUTO_COMPLETE_VIEW_FREE:
      return {
        filter: "agTextColumnFilter",
        valueFormatter: (params: any) => params.value?.label || params.value,
        valueGetter: (params: any) => params.value?.id
      };
    case TypeSimpleComponent.SYS_DOMAINE:
      return {
        filter: "agSetColumnFilter",
        filterValues: compo.sysDomaineChoices,
        valueFormatter: (params: any) => {
          const choice = compo.sysDomaineChoices?.find(c => c.value === params.value);
          return choice?.label || params.value;
        }
      };
    case TypeSimpleComponent.EDITOR:
      return {
        filter: "agTextColumnFilter",
        cellEditor: "agLargeTextCellEditor",
        cellEditorPopup: true
      };
    case TypeSimpleComponent.INPUT:
      return { filter: "agTextColumnFilter" };
    case TypeSimpleComponent.CHECKBOX:
      return {
        filter: "agSetColumnFilter",
        filterValues: ["true", "false"],
        renderer: CheckBoxRenderer,
        filterRenderer: CheckboxFilterRenderer
      };
    case TypeSimpleComponent.CALENDAR:
    case TypeSimpleComponent.CALENDAR_HOUR:
    case TypeSimpleComponent.CALENDAR_HOUR_SECOND:
      return {
        filter: "agDateColumnFilter",
        cellEditor: "agDateCellEditor",
        valueFormatter: (params: any) =>
          params.value ? new Date(params.value).toLocaleDateString() : ""
      };
    case TypeSimpleComponent.OEL:
      return { filter: false, renderer: buildHtmlRenderer(compo) };
    case TypeSimpleComponent.INPUT_NUMBER:
      return { filter: "agNumberColumnFilter", cellEditor: "agNumberCellEditor" };
    default:
      return { filter: false };
  }
}

function isEditable(column: ComponentState) {
  return (params: any) => {
    if (column.typeCompo !== TypeSimpleComponent.OEL) {
      if (params.data && params.data.id) {
        return (
          (column.readOnly === ComponentReadOnly.DEFAULT ||
            column.readOnly === ComponentReadOnly.READ_ONLY_INSERT) &&
          (column.disabled === ComponentReadOnly.DEFAULT ||
            column.disabled === ComponentReadOnly.READ_ONLY_INSERT)
        );
      } else {
        return (
          (column.readOnly === ComponentReadOnly.DEFAULT ||
            column.readOnly === ComponentReadOnly.READ_ONLY_UPDATE) &&
          (column.disabled === ComponentReadOnly.DEFAULT ||
            column.disabled === ComponentReadOnly.READ_ONLY_UPDATE)
        );
      }
    } else {
      return false;
    }
  };
}

function getCellStyle(column: ComponentState) {
  return (params: any) => {
    if (
      params.data &&
      column.oel &&
      column.typeCompo !== TypeSimpleComponent.OEL &&
      params.data._style
    ) {
      return { ...params.data._style[column.column] };
    }
    return {};
  };
}

function getCellClasssesByWvi(wviType: string | undefined) {
  switch (wviType) {
    case "danger":
      return tw`border border-red-500`;
    case "warning":
      return tw`border border-yellow-500`;
    case "success":
      return tw`border border-green-500`;
    default:
      return undefined;
  }
}

export function flatDataFromPopjo(pojo: Partial<Pojo>): Partial<Pojo> {
  return Object.entries(pojo).reduce((acc, [key, value]) => {
    if (value && typeof value === "object") {
      acc[key] = (value as any).id;
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});
}

export function mapOneFilter(condition: Filtermodel, field: string): GS {
  let value: string | Date = condition.filter;
  let operator: RSQLOperator = "OPER_EQ";
  if (condition.filterType === "text") {
    switch (condition.type) {
      case "contains":
        operator = "OPER_LIKE_ANYWHERE";
        value = convertTermByOperator(value, "OPER_LIKE_ANYWHERE");
        break;
      case "notContains":
        operator = "OPER_DOES_NOT_CONTAIN";
        //value = convertTermByOperator(value, "OPER_DOES_NOT_CONTAIN");
        break;
      case "equals":
        operator = "OPER_EQ";
        break;
      case "notEqual":
        operator = "OPER_NE";
        break;
      case "startsWith":
        operator = "OPER_LIKE_START";
        value = convertTermByOperator(value, "OPER_LIKE_START");
        break;
      case "endsWith":
        operator = "OPER_LIKE_END";
        value = convertTermByOperator(value, "OPER_LIKE_END");
        break;
      case "blank":
        operator = "OPER_NULL";
        break;
      case "notBlank":
        operator = "OPER_NOT_NULL";
        break;
      default:
        operator = "OPER_EQ";
        break;
    }
  } else if (condition.filterType === "date") {
    switch (condition.type) {
      case "equals":
        operator = "OPER_EQ";
        if (condition.dateFrom) {
          value = parse(condition.dateFrom);
        }
        break;
      case "notEqual":
        operator = "OPER_NE";
        if (condition.dateFrom) {
          value = parse(condition.dateFrom);
        }
        break;
      case "lessThan":
        operator = "OPER_LT";
        if (condition.dateFrom) {
          value = parse(condition.dateFrom);
        }
        break;
      case "greaterThan":
        operator = "OPER_GT";
        if (condition.dateFrom) {
          value = parse(condition.dateFrom);
        }
        break;
      case "inRange":
        if (condition.dateTo && condition.dateFrom) {
          const crit = GSBuilder.AND(
            GSBuilder.Comparison(field, "OPER_LT", parse(condition.dateTo)),
            GSBuilder.Comparison(field, "OPER_GT", parse(condition.dateFrom))
          );
          return crit;
        }
        break;
      case "blank":
        operator = "OPER_NULL";
        break;
      case "notBlank":
        operator = "OPER_NOT_NULL";
        break;
      default:
        operator = "OPER_EQ";
    }
  } else if (condition.filterType === "number") {
    switch (condition.type) {
      case "equals":
        operator = "OPER_EQ";
        break;
      case "notEqual":
        operator = "OPER_NE";
        break;
      case "lessThan":
        operator = "OPER_LT";
        break;
      case "greaterThan":
        operator = "OPER_GT";
        break;
      case "lessThanOrEqual":
        operator = "OPER_LE";
        break;
      case "greaterThanOrEqual":
        operator = "OPER_GE";
        break;
      case "inRange":
        return GSBuilder.AND(
          GSBuilder.Comparison(field, "OPER_LE", condition.filterTo),
          GSBuilder.Comparison(field, "OPER_GE", condition.filter)
        );
      case "blank":
        operator = "OPER_NULL";
        break;
      case "notBlank":
        operator = "OPER_NOT_NULL";
        break;
      default:
        operator = "OPER_EQ";
    }
  } else if (condition.filterType === "set") {
    if (condition.values && condition.values.length > 0) {
      return GSBuilder.Comparison(field, "OPER_IN", condition.values);
    }
  }
  return GSBuilder.Comparison(field, operator, value);
}

export function mapcriteriaForGS(col: ComponentState, condition: Filtermodel, field: string): GS {
  const value = condition.filter;

  const correctedNode = GSBuilder.visit<GS>(col.additionalClause, {
    And: (node, visitor) => {
      let result: GS[] = [];
      for (let n of node.nodes) {
        let visitedNode = GSBuilder.visit(n, visitor);
        if (visitedNode) result.push(visitedNode);
      }
      return GSBuilder.AND(...result);
    },
    Or: (node, visitor) => {
      let result: GS[] = [];
      for (let n of node.nodes) {
        let visitedNode = GSBuilder.visit(n, visitor);
        if (visitedNode) result.push(visitedNode);
      }
      return GSBuilder.OR(...result);
    },
    Comparison: param => {
      const criteria = mapOneFilter(condition, `${field}.${param.field}`);
      return {
        ...criteria,
        field: `${field}.${param.field}`,
        value:
          param.value !== "" && param.value !== undefined && param.value !== null
            ? param.value
            : convertTermByOperator(value, (criteria as GSComparison).operator)
      };
    }
  });
  return correctedNode as GS;
}

function mapTypeCompoToFilterType(typeCompo: TypeSimpleComponentValue) {
  switch (typeCompo) {
    case TypeSimpleComponent.CALENDAR:
    case TypeSimpleComponent.CALENDAR_HOUR:
    case TypeSimpleComponent.CALENDAR_HOUR_SECOND:
      return "date";
    case TypeSimpleComponent.INPUT_NUMBER:
      return "number";
    case TypeSimpleComponent.CHECKBOX:
    case TypeSimpleComponent.SYS_DOMAINE:
      return "set";
    default:
      return "text";
  }
}

const gridStyle = { height: "100%", width: "100%", padding: "0" };

const AgGridContainer = forwardRef<AgGridReact, DatatableAGGridProps>(
  (
    {
      sjmoCode,
      tableName,
      ctrlkey,
      additionnalClause,
      definition,
      currentFocus,
      dirtyRows,
      insertable,
      wviState,
      onChange,
      saveRows,
      deleteRows,
      onAfterSaveDatatable,
      onAfterDeleteDatatable,
      getRSQLFilter,
      fetchData,
      onSaveFocusPerso,
      onRazFocusPerso,
      onFocusChange,
      onColumnMoved,
      onRowClick,
      setDirtyRows,
      onColumnResized
    },
    ref
  ) => {
    const gridRef = ref as MutableRefObject<AgGridReact<any> | null>;
    const { t } = useTranslation();
    const interactions = useInteractions({ sjmoCode, ctrlKey: ctrlkey, tableName });
    const lang = sessionStorage.getItem("userLang") || "fr";

    const oelColumns = useMemo(() => {
      return definition.columns
        .filter(c => c.typeCompo === TypeSimpleComponent.OEL && c.compoVisible)
        .map(c => c.column);
    }, [definition.columns]);

    const [selectedRows, setSelectedRows] = useState<Pojo[]>([]);
    const [selectAll, setSelectAll] = useState(false);
    const [filterBar, setFilterBar] = useState<FilterBar | undefined>(undefined);
    const [filterAllFields, setFilterAllFields] = useState<string | undefined>(undefined);
    const [{ currentFilter, rowCount, columnFilter }, setCurrentFilter] = useState<{
      currentFilter: string | undefined;
      rowCount: number;
      columnFilter: string[];
    }>({ currentFilter: undefined, rowCount: 0, columnFilter: [] });
    const [existSatellite, setExistSatellite] = useState<Record<string, boolean>>({});

    const [gridPadding, setGridPadding] = useState<"small" | "large">("large");
    const [breakRows, setBreakRows] = useState<string[]>([]);

    const { flatSelectedRows, selectedNumber } = useMemo(() => {
      const flatSelectedRows = selectedRows.map(r => flatDataFromPopjo(r));
      const selectedNumber = flatSelectedRows.length;
      return { flatSelectedRows, selectedNumber };
    }, [selectedRows]);

    const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
      setSelectedRows(event.api.getSelectedNodes().map(node => node.data));
    }, []);

    const localeText = useMemo(() => {
      switch (lang) {
        case "en":
          return AG_GRID_LOCALE_EN;
        case "de":
          return AG_GRID_LOCALE_DE;
        case "it":
          return AG_GRID_LOCALE_IT;
        default:
          return AG_GRID_LOCALE_FR;
      }
    }, [lang]);

    useEffect(() => {
      // récupéation et gestion du type du filtre privilégié
      const filterPrivilegie = (currentFocus?.filters.find(
        f => f.privilegie
      ) as any) as FilterBar | null;
      if (filterPrivilegie) {
        setFilterBar({
          filterBarId: filterPrivilegie.filterBarId,
          startDate: filterPrivilegie.startDate,
          endDate: filterPrivilegie.endDate
        });
      }
    }, [currentFocus]);

    const columnDefs = useMemo(() => {
      const columns = [];

      columns.push({
        field: "satellite",
        headerName: "",
        sortable: false,
        filter: false,
        width: 50,
        contextMenuItems: [],
        cellRenderer: SateliteMenuRenderer({
          sjmoCode,
          ctrlkey,
          currentFilter,
          columns: definition.columns,
          existSatellite
        })
      });

      columns.push(
        ...definition.columns
          .filter(c => c.compoVisible)
          .map(column => {
            const {
              filter,
              filterValues,
              renderer,
              filterRenderer,
              cellEditor,
              cellEditorPopup,
              valueFormatter
            } = getPropsByTypeCompo({
              typeCompo: column.typeCompo,
              tableName,
              sjmoCode,
              ctrlKey: ctrlkey,
              componentID: column.id,
              columnIndex: definition.columns.indexOf(column),
              compo: column
            });

            const breakColumns = [...breakRows, ...(currentFocus?.breakRows ?? [])];

            return {
              field: column.column,
              headerName: column.label,
              sortable: column.typeCompo !== TypeSimpleComponent.OEL,
              resizable: true,
              editable: saveRows ? isEditable(column) : false,
              cellRenderer: renderer,
              cellEditor: saveRows ? cellEditor : undefined,
              cellEditorPopup: saveRows ? cellEditorPopup : undefined,
              width: Math.max(column.contentSize, column.labelSize),
              filter: filter,
              filterParams: {
                values: filterValues,
                keyCreator:
                  filterValues && filterValues.length > 0 && filterValues[0] instanceof Object
                    ? getValueFromSd
                    : undefined,
                valueFormatter:
                  filterValues && filterValues.length > 0 && filterValues[0] instanceof Object
                    ? getLabelFromSd
                    : undefined,
                cellRenderer: filterRenderer,
                buttons: ["apply", "reset"]
              },
              rowGroup: breakColumns.includes(column.column),
              hide: breakColumns.includes(column.column),
              cellStyle: getCellStyle(column),
              cellClass: (params: CellClassParams) => {
                const wviType = wviState?.[params.rowIndex]?.[column.column]?.type?.toLowerCase();
                return getCellClasssesByWvi(wviType);
              },
              valueFormatter,
              valueSetter: onChange
            };
          })
      );
      return columns;
    }, [
      breakRows,
      ctrlkey,
      currentFilter,
      currentFocus?.breakRows,
      definition.columns,
      existSatellite,
      onChange,
      saveRows,
      sjmoCode,
      tableName,
      wviState
    ]);

    const allColumnRef = useMemo(() => {
      return definition.columns.reduce((acc, column) => {
        acc[column.column] = column;
        return acc;
      }, {} as Record<string, ComponentState>);
    }, [definition.columns]);

    const autoGroupColumnDef = useMemo<ColDef>(() => {
      return {
        minWidth: 200
      };
    }, []);

    const getRows = useCallback(
      (params: IServerSideGetRowsParams) => {
        const localSortModel: Record<string, DatatableSort> = params.request.sortModel?.reduce(
          (acc, { colId, sort }) => {
            acc[colId] = sort === "asc" ? "ASC" : "DESC";
            return acc;
          },
          {} as Record<string, DatatableSort>
        );

        const rsql = getRSQLFilter(
          interactions,
          params.request.filterModel as AGFilter,
          params.request.sortModel && params.request.sortModel.length > 0
            ? localSortModel
            : currentFocus?.sort
            ? parseSortStr(currentFocus.sort)
            : {},
          [...breakRows, ...(currentFocus?.breakRows ?? [])],
          additionnalClause,
          allColumnRef
        );

        const groupBy = params.request.rowGroupCols ?? [];
        const groupKeys = params.request.groupKeys ?? [];

        if (filterAllFields && filterAllFields.length > 0) {
          const filtered = getFilterAllField(definition, filterAllFields);
          rsql.filters.and(filtered);
        }

        // Ajout des critères de filtre pour le groupement
        if (groupBy.length > 0 && groupKeys.length > 0) {
          const filtered = getGroupByFilter(
            groupBy,
            groupKeys,
            rsql,
            params.request.groupKeys ?? []
          );
          if (filtered) {
            rsql.filters.and(filtered);
          }
        }

        const groupByStr =
          groupBy.length > 0 && groupBy.length > groupKeys.length
            ? groupBy[groupKeys.length].field
            : undefined;

        fetchData({
          sjmoCode,
          tableName,
          ctrlkey,
          currentFocus,
          startIndex: params.request.startRow ?? 0,
          stopIndex: params.request.endRow ?? MIN_BATCH_SIZE,
          reset: false,
          cancelToken: Axios.CancelToken.source(),
          oelColumns,
          query: rsql.build(),
          filterBar,
          groupBy: groupByStr
        })
          .then(res => {
            // On ne garde que les colonnes présentes dans rowGroupCols si spécifié
            let result: Record<string, any>[] = res.data;
            if (
              params.request.rowGroupCols?.length > 0 &&
              params.request.rowGroupCols.length !== params.request.groupKeys.length
            ) {
              result = res.data.map(row => {
                const groupedRow = {};

                params.request.rowGroupCols.forEach(col => {
                  groupedRow[col.field as string] = row[col.field as string] ?? "";
                });

                // il faut prendre l'idnex en fonction des groupes ouverts
                const index =
                  params.request.groupKeys && params.request.groupKeys.length > 0
                    ? params.request.groupKeys.length
                    : 0;

                const idValues = params.request.rowGroupCols.slice(0, index + 1).map(col => {
                  const value = row[col.field as string] ?? "";
                  return typeof value === "object" ? `${value.id}` : `${value}`;
                });
                groupedRow["id"] = idValues.join("_");
                return groupedRow;
              });

              // Filtrer pour n'avoir que des IDs uniques
              result = result.filter(
                (row, index, self) => index === self.findIndex(r => r.id === row.id)
              );
            }

            params.success({
              rowData: result,
              rowCount: res.meta.totalRecords
            });

            existList({ tableName, ids: res.data.map(d => d.id) }).then(satelliteExistResponse => {
              setExistSatellite(x => ({ ...x, ...satelliteExistResponse.data }));
            });

            const columnFilter = explainFilter(
              params.request.filterModel as AGFilter,
              allColumnRef,
              localeText
            );

            setCurrentFilter({
              currentFilter: rsql.build(),
              rowCount: res.meta.totalRecordsWithoutGrouping ?? res.meta.totalRecords,
              columnFilter: columnFilter
            });
          })
          .catch(error => {
            console.error("error during the getRows", error);
            params.fail();
          });
      },
      [
        getRSQLFilter,
        interactions,
        currentFocus,
        breakRows,
        additionnalClause,
        allColumnRef,
        filterAllFields,
        fetchData,
        sjmoCode,
        tableName,
        ctrlkey,
        oelColumns,
        filterBar,
        definition,
        localeText
      ]
    );

    const onGridReady = useCallback(
      (params: GridReadyEvent) => {
        const datasource = { getRows: getRows };
        params.api.setGridOption("serverSideDatasource", datasource);
      },
      [getRows]
    );

    // refresh lors du changement de filtre, on utilise un useeffect car il faut lancer le refresh après avoir mis à jours le state
    const refreshData = useCallback(() => {
      setDirtyRows && setDirtyRows({});
      gridRef?.current?.api?.setGridOption("serverSideDatasource", { getRows: getRows });
    }, [getRows, gridRef, setDirtyRows]);
    useEffect(() => {
      setDirtyRows && setDirtyRows({});
      gridRef?.current?.api?.setGridOption("serverSideDatasource", { getRows: getRows });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getRows, setDirtyRows]);

    const isApiPendingRef = useRef(false);
    const onSave = useCallback(async () => {
      if (!saveRows || !dirtyRows) {
        return;
      }
      track("datatable::save", { status: "started" });

      if (isApiPendingRef.current === true) {
        track("datatable::save", { status: "spam" });
        return Promise.resolve();
      } else {
        isApiPendingRef.current = true;
      }

      return saveRows(Object.values(dirtyRows))
        .then(() => {
          refreshData();
          track("datatable::save", { status: "success" });
          // TODO: lancer les refresh de galaxy
          onAfterSaveDatatable && onAfterSaveDatatable();
        })
        .catch(e => {
          // dispatch({ type: "SAVE_ERROR" });
          track("datatable::save", { status: "error" });
          console.error("error during save");
          console.error(e);
        })
        .finally(() => {
          isApiPendingRef.current = false;
        });
    }, [dirtyRows, onAfterSaveDatatable, refreshData, saveRows]);

    const onDelete = useCallback(async () => {
      if (!deleteRows) {
        return;
      }

      if (isApiPendingRef.current === true) {
        track("datatable::delete", { status: "spam" });
        return Promise.resolve();
      } else {
        isApiPendingRef.current = true;
      }

      return deleteRows(selectedRows)
        .then(() => {
          refreshData();
          onAfterDeleteDatatable && onAfterDeleteDatatable();
        })
        .catch(e => {
          track("datatable::delete", { status: "error" });
          console.error("error during delete");
          console.error(e);
        })
        .finally(() => {
          isApiPendingRef.current = false;
        });
    }, [deleteRows, onAfterDeleteDatatable, refreshData, selectedRows]);

    const getMainMenuItems = useCallback((params: GetMainMenuItemsParams) => {
      return [
        params.defaultItems[0],
        params.defaultItems[1],
        {
          name: "Maintenez 'shift' pour trier sur plusieurs colonnes"
        },
        ...params.defaultItems.slice(2)
      ] as any;
    }, []);

    const getContextMenuItems = useCallback(
      async (params: GetContextMenuItemsParams) => {
        const rowData = params.node?.data;
        const id = rowData?.id;
        const joinId = typeof params?.value === "object" ? params?.value?.id : null;
        const tableName = rowData?.tableName;
        const joinTableName = typeof params?.value === "object" ? params?.value?.tableName : null;
        const contextColumnName = (params.column as any)?.colId;

        const res = await getProcessusDefinition(sjmoCode, joinTableName ?? tableName);

        const traitements = res.data.filter(it => it.type === "traitement");
        const navigations = res.data.filter(it => it.type === "navigation");
        const editions = res.data.filter(it => it.type === "edition");
        const menuItems = [];

        if (traitements.length > 0) {
          menuItems.push({
            name: "Traitements",
            subMenu: traitements.map(traitement => ({
              name: traitement.label,
              menuItem: MenuItem,
              menuItemParams: {
                buttonValue: traitement.label,
                definition: traitement,
                selected: joinId ? params.value : rowData,
                sjmoCode,
                tableName
              }
            }))
          });
        }

        if (navigations.length > 0) {
          menuItems.push({
            name: "Navigations",
            subMenu: navigations.map(navigation => ({
              name: navigation.label,
              menuItem: MenuItem,
              menuItemParams: {
                buttonValue: navigation.label,
                definition: navigation,
                selected: joinId ? params.value : rowData,
                sjmoCode,
                tableName
              }
            }))
          });
        }

        if (editions.length > 0) {
          menuItems.push({
            name: "Editions",
            subMenu: editions.map(edition => ({
              name: edition.label,
              menuItem: MenuItem,
              menuItemParams: {
                buttonValue: edition.label,
                definition: edition,
                selected: joinId ? params.value : rowData,
                sjmoCode,
                tableName
              }
            }))
          });
        }
        menuItems.push("separator");

        menuItems.push({
          name: t("commun_miniExpert"),
          menuItem: MiniExpertMenuItem,
          menuItemParams: {
            sjmoCode,
            tableName: joinTableName ?? tableName,
            id: joinId ?? id
          }
        });

        menuItems.push({
          name: t("commun_information"),
          menuItem: InfoMenuItem,
          menuItemParams: {
            tableName,
            joinId,
            id,
            contextColumnName
          }
        });

        return menuItems;
      },
      [sjmoCode, t]
    );

    const onAdd = useCallback(() => {
      if (!insertable) {
        return;
      }
      preRecord({
        sjmoCode,
        tableName,
        focusId: definition.selectedFocus,
        context: interactions
      })
        .then(res => {
          const transaction: ServerSideTransaction = {
            addIndex: 0,
            add: [res.data]
          };
          gridRef?.current?.api?.applyServerSideTransaction(transaction);
        })
        .catch(() => {
          console.error("erreur de l'initialisation d'une nouvelle ligne");
        });
    }, [insertable, sjmoCode, tableName, definition.selectedFocus, interactions, gridRef]);

    const rowClassRules = useMemo<RowClassRules>(() => {
      const dirtyRowsKeys = Object.keys(dirtyRows ?? {});
      return {
        [`${tw("border-l-2 border-blue-500")}`]: (params: any) => {
          return params.data && dirtyRowsKeys.includes(params.data.id);
        }
      };
    }, [dirtyRows]);

    const onRowCliked = useCallback(
      (params: RowClickedEvent) => {
        // on ne clique pas sur les groupes
        onRowClick && !params.node.group && onRowClick(params);
      },
      [onRowClick]
    );

    if (!currentFocus) return <div />;
    return (
      <AutoSizer disableWidth>
        {({ width, height }) => (
          <ProcessusProvider
            sjmoCode={sjmoCode}
            tableName={tableName}
            selected={flatSelectedRows as Pojo[]}
          >
            <AgTableToolbar
              sjmoCode={sjmoCode}
              tableName={tableName}
              ctrlkey={ctrlkey}
              focus={definition.focus}
              selectedFocus={definition.selectedFocus}
              selectedRows={flatSelectedRows as Pojo[]}
              filterable={
                definition.focus.find(f => f.focusId === definition.selectedFocus)?.filterable ??
                false
              }
              filters={
                definition.focus.find(f => f.focusId === definition.selectedFocus)?.filters ?? []
              }
              filterBar={filterBar}
              columns={definition.columns}
              currentFilter={currentFilter}
              lastFilterFetched={currentFilter}
              columnFilter={columnFilter}
              gridPadding={gridPadding}
              breakRows={breakRows}
              isDirty={(dirtyRows && Object.keys(dirtyRows).length > 0) ?? false}
              selectAll={selectAll}
              rowCount={rowCount}
              selectedNumber={selectAll ? rowCount : selectedNumber}
              setSelectAll={setSelectAll}
              setGridPadding={setGridPadding}
              refresh={refreshData}
              onFilterChange={({ filterBarId, startDate, endDate, filterBarDefaultDtFilter }) => {
                setFilterBar({
                  filterBarId,
                  startDate,
                  endDate,
                  filterBarDefaultDtFilter
                });
              }}
              onFocusChange={onFocusChange}
              onSaveFocusPerso={onSaveFocusPerso}
              onRazFocusPerso={onRazFocusPerso}
              onSave={saveRows ? onSave : undefined}
              onDelete={deleteRows ? onDelete : undefined}
              setFilterAllFields={setFilterAllFields}
              setBreakRows={setBreakRows}
              onAdd={insertable ? onAdd : undefined}
              clearColumnFilter={() => {
                // Vider tous les filtres de colonnes
                gridRef?.current?.api?.setFilterModel(null);
                // Pas besion de mettre à jour currentFilter on lance le refresh des données
              }}
            />
            <div style={{ width: width, height: height - 60 }}>
              <div style={gridStyle}>
                <AgGridReact<Pojo>
                  ref={ref}
                  columnDefs={columnDefs as any}
                  domLayout="normal"
                  rowModelType="serverSide"
                  autoGroupColumnDef={autoGroupColumnDef}
                  rowClassRules={rowClassRules}
                  groupAllowUnbalanced={true}
                  getRowId={getRowId}
                  onGridReady={onGridReady}
                  cacheBlockSize={25} // Nombre d'éléments par requête
                  blockLoadDebounceMillis={10} // Délai avant de déclencher une nouvelle requête
                  maxBlocksInCache={100}
                  localeText={localeText}
                  getMainMenuItems={getMainMenuItems}
                  getContextMenuItems={getContextMenuItems as any}
                  rowSelection={selectAll ? undefined : ROW_SELECTION_MODE}
                  onSelectionChanged={selectAll ? undefined : onSelectionChanged}
                  onColumnMoved={onColumnMoved}
                  rowHeight={gridPadding === "large" ? undefined : 25}
                  onRowClicked={onRowCliked}
                  selectionColumnDef={{}}
                  onColumnResized={onColumnResized}
                />
              </div>
            </div>
          </ProcessusProvider>
        )}
      </AutoSizer>
    );
  }
);

// Créer un composant personnalisé pour le rendu HTML
function buildHtmlRenderer(compo: ComponentState) {
  return (props: any) => {
    return <OelForTable name={props.column.field} value={props.value} {...compo.options} />;
  };
}

const CheckBoxRenderer: FC<Partial<CustomCellRendererProps>> = props => {
  return (
    <div>
      <Switch
        id={`${props.data?.id}-${props.colDef?.field}`}
        name={props.colDef?.field}
        //intent={getIntent(props.wviState)}
        disabled={true}
        checked={props.value}
        data-value={props.value}
        onCheckedChange={val => {}}
        onContextMenu={() => {}}
      />
    </div>
  );
};

const CheckboxFilterRenderer: FC<any> = props => {
  // valeur définie en dur par aggrid
  if (props.value === "(Sélectionner tout)") {
    return <span>Tous choisir</span>;
  }
  return (
    <CheckBoxRenderer
      value={props.value === "true"}
      colDef={props.colDef}
      data={{ id: `filter-${props.value}` }}
    />
  );
};

function SateliteMenuRenderer({
  sjmoCode,
  ctrlkey,
  currentFilter,
  columns,
  existSatellite
}: {
  sjmoCode: string;
  ctrlkey: string;
  currentFilter?: string;
  columns: ComponentState[];
  existSatellite: Record<string, boolean>;
}) {
  return (props: any) => {
    if (props.data && props.data.id && props.data.tableName) {
      const existSatelliteData =
        (existSatellite && props.data && existSatellite[props.data.id]) || false;

      return (
        <SatelliteMenuButton
          isActive={props.data.id && props.data.id.split("~").length < 4 ? true : false}
          sjmoCode={sjmoCode}
          tableName={props.data.tableName}
          entityId={props.data.id}
          ctrlKey={ctrlkey}
          filter={currentFilter}
          columns={columns.slice(0, 4).map(col => col.column)}
          className={tw(
            `bg-transparent border-0 align-baseline p-0 ${
              existSatelliteData ? "text-blue-500" : "text-gray-500 hover:text-black"
            }`
          )}
          style={{
            height: 25
          }}
        >
          <Fa icon={[existSatelliteData ? "fad" : "far", "satellite"]} />
        </SatelliteMenuButton>
      );
    } else {
      return <div />;
    }
  };
}

export default AgGridContainer;
